// Pluggable scanner hook that runs after `decide` accepts the structural
// shape of a payload. The policy can prove "this is a PNG of legal size";
// the scanner is for content-aware checks that need the payload itself —
// decompression bombs, magic-byte / metadata anomalies, or future on-device
// ML classifiers.
//
// Scanners are async so future scanners can do disk or network work
// (e.g. a WASM-based malware classifier). The default DecompressionBomb
// scanner is pure CPU and ignores `await`. First non-clean verdict wins,
// composed left-to-right in `AttachmentScanCoordinator`, so a host can
// stack cheap detectors before expensive ones and bail early.
//
// Web port of `kaether_for_ios/Stoat/E2EE/AttachmentScanHook.swift`.

import type { AttachmentCategory } from "./AttachmentPolicy";

// ---------------------------------------------------------------------------
// Verdict type
// ---------------------------------------------------------------------------

export type AttachmentScanVerdict =
  | { readonly kind: "clean" }
  | { readonly kind: "suspicious"; readonly reason: string }
  | { readonly kind: "blocked"; readonly reason: string };

export const scanClean: AttachmentScanVerdict = { kind: "clean" };

export function scanSuspicious(reason: string): AttachmentScanVerdict {
  return { kind: "suspicious", reason };
}

export function scanBlocked(reason: string): AttachmentScanVerdict {
  return { kind: "blocked", reason };
}

/** Helper to compare verdicts (kind + reason for non-clean). */
export function verdictEquals(
  a: AttachmentScanVerdict,
  b: AttachmentScanVerdict,
): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "clean") return true;
  return (a as { reason: string }).reason === (b as { reason: string }).reason;
}

// ---------------------------------------------------------------------------
// Scanner interface
// ---------------------------------------------------------------------------

/**
 * Inspect a plaintext payload (post-decrypt on inbound, pre-encrypt on
 * outbound). `claimedDeclaredSize` is the byte count the sender declared for
 * the payload; the default decompression-bomb scanner uses it to detect
 * 100:1 compression-ratio outliers.
 */
export interface AttachmentScanner {
  scan(
    category: AttachmentCategory,
    plaintextBytes: Uint8Array,
    claimedDeclaredSize: number,
  ): Promise<AttachmentScanVerdict>;
}

// ---------------------------------------------------------------------------
// DecompressionBombScanner
// ---------------------------------------------------------------------------

/**
 * Catches decompression bombs and size-claim undershoots by comparing the
 * payload we actually got to the size the sender said we would. Pure
 * arithmetic — does not parse the payload, so it works for any category.
 *
 * Flags two suspicious patterns:
 *   - `claimedDeclaredSize > actual × 100` — zip-bomb / compressed-image
 *     bomb pattern (100x ratio outlier).
 *   - `actual > claimedDeclaredSize × 2` — size-claim undershoot (sender
 *     claimed small, shipped large).
 */
export class DecompressionBombScanner implements AttachmentScanner {
  /** Threshold for "claim says many-times-larger than what we got." */
  static readonly RATIO_OUTLIER_MULTIPLIER = 100;

  /**
   * Threshold for "payload is many-times-larger than what we were told to
   * expect." A 2x mismatch is sufficient to flag — legitimate paths either
   * pass the correct size or pass zero (which never triggers).
   */
  static readonly UNDERSHOOT_MULTIPLIER = 2;

  async scan(
    _category: AttachmentCategory,
    plaintextBytes: Uint8Array,
    claimedDeclaredSize: number,
  ): Promise<AttachmentScanVerdict> {
    const actual = plaintextBytes.length;

    if (claimedDeclaredSize > actual * DecompressionBombScanner.RATIO_OUTLIER_MULTIPLIER) {
      return scanSuspicious("decompression-ratio outlier");
    }
    if (actual > claimedDeclaredSize * DecompressionBombScanner.UNDERSHOOT_MULTIPLIER) {
      return scanSuspicious("size-claim-undershoot");
    }
    return scanClean;
  }
}

// ---------------------------------------------------------------------------
// AlwaysCleanScanner
// ---------------------------------------------------------------------------

/**
 * Test fixture + no-op fallback for hosts that have not yet registered a
 * real scanner. Always returns `clean`; tests for the coordinator rely on
 * this to assert that a clean scanner does not mask a later detector.
 */
export class AlwaysCleanScanner implements AttachmentScanner {
  async scan(
    _category: AttachmentCategory,
    _plaintextBytes: Uint8Array,
    _claimedDeclaredSize: number,
  ): Promise<AttachmentScanVerdict> {
    return scanClean;
  }
}

// ---------------------------------------------------------------------------
// AttachmentScanCoordinator
// ---------------------------------------------------------------------------

/**
 * Runs a fixed list of scanners in order and returns the first non-clean
 * verdict. Composition is left-to-right so callers can place cheap
 * detectors before expensive ones. An empty scanner list returns
 * `clean` — equivalent to "no scanner registered, trust the policy."
 */
export class AttachmentScanCoordinator {
  private readonly scanners: readonly AttachmentScanner[];

  constructor(scanners: readonly AttachmentScanner[]) {
    this.scanners = scanners;
  }

  async run(
    category: AttachmentCategory,
    plaintextBytes: Uint8Array,
    declaredSize: number,
  ): Promise<AttachmentScanVerdict> {
    for (const scanner of this.scanners) {
      const verdict = await scanner.scan(category, plaintextBytes, declaredSize);
      if (verdict.kind !== "clean") {
        return verdict;
      }
    }
    return scanClean;
  }
}
